import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MultiDatePicker from '../components/MultiDatePicker';
import { Calendar, Clock, Plus, Trash2, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSTRUCTORS = [
  { id: 'julie-christiansen', name: 'Julie Christiansen - Online Pivo + Live in Heiden' },
  { id: 'julie-greven-weekend', name: 'Julie Christiansen - Weekendkurser Heiden' },
  { id: 'julie-weekend-external', name: 'Julie Christiansen - Weekendkurser (Udkørende)' },
  { id: 'oscar-kristjansson', name: 'Óskar Erik Kristjánsson' }
];

const AvailabilityCreatePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [formData, setFormData] = useState({
    instructor_id: '',
    instructor_name: '',
    dates: [],
    start_time: '09:00',
    end_time: '21:00',
    duration_minutes: 40,
    pause_start: '',
    pause_end: ''
  });

  // Date handling is now done through MultiDatePicker component

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out empty dates
    const validDates = formData.dates.filter(d => d.trim() !== '');
    
    if (validDates.length === 0) {
      toast.error('Please add at least one date');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/availability/bulk`,
        {
          instructor_id: formData.instructor_id,
          dates: validDates,
          start_time: formData.start_time,
          end_time: formData.end_time,
          duration_minutes: parseInt(formData.duration_minutes)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { created, errors, total_created } = response.data;
      
      if (total_created > 0) {
        toast.success(`${total_created} date(s) created successfully!`);
      }
      
      if (errors.length > 0) {
        errors.forEach(error => toast.warning(error));
      }

      // Reset form or navigate back
      navigate('/admin');
    } catch (error) {
      console.error('Error creating availability:', error);
      toast.error(error.response?.data?.detail || 'Failed to create availability');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
          {/* Back button */}
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#7c6f5e',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              padding: '0.5rem',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.7'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            <ArrowLeft size={20} />
            Back to Admin Dashboard
          </button>

          <h1 className="booking-title" style={{ marginBottom: '0.5rem' }}>
            Create Availability
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            Tilføj tilgængelige datoer og tider
          </p>

          <form onSubmit={handleSubmit}>
            {/* Instructor Selection */}
            <div className="form-group">
              <label className="form-label">
                <Calendar size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Instructor
              </label>
              
              <select
                className="form-input"
                value={formData.instructor_id}
                onChange={(e) => setFormData({
                  ...formData,
                  instructor_id: e.target.value,
                  instructor_name: INSTRUCTORS.find(i => i.id === e.target.value)?.name || ''
                })}
                required
              >
                <option value="">Vælg Instructor</option>
                {INSTRUCTORS.map((instructor) => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Dates */}
            <div className="form-group">
              <label className="form-label">
                Tilgængelige Datoer
              </label>
              
              <button
                type="button"
                onClick={() => setShowDatePicker(true)}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: '#7c6f5e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                  marginBottom: '1rem'
                }}
              >
                <Calendar size={20} />
                {formData.dates.length === 0 
                  ? 'Vælg Datoer fra Kalender' 
                  : `${formData.dates.length} Dato${formData.dates.length !== 1 ? 'er' : ''} Valgt - Klik for at ændre`
                }
              </button>

              {/* Show selected dates */}
              {formData.dates.length > 0 && (
                <div style={{
                  background: '#e8f5e9',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                    Valgte datoer:
                  </strong>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formData.dates.sort().map((date) => (
                      <span
                        key={date}
                        style={{
                          background: '#4caf50',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        {new Date(date + 'T00:00:00').toLocaleDateString('da-DK', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Time Range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">
                  <Clock size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Start Time
                </label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  step="300"
                  required
                />
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                  5-minute increments
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Clock size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  End Time
                </label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  step="300"
                  required
                />
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                  5-minute increments
                </p>
              </div>
            </div>

            {/* Duration */}
            <div className="form-group">
              <label className="form-label">Lesson Duration (minutes)</label>
              <select
                className="form-input"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                required
              >
                <option value="30">30 minutes</option>
                <option value="40">40 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
                <option value="120">120 minutes</option>
              </select>
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                Time slots will be created automatically based on this duration
              </p>
            </div>

            {/* Pause (optional) */}
            <div style={{ 
              background: '#f5f5f5', 
              padding: '1rem', 
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                Lunch Break (Optional)
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Pause Start</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.pause_start}
                    onChange={(e) => setFormData({ ...formData, pause_start: e.target.value })}
                    step="300"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Pause End</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.pause_end}
                    onChange={(e) => setFormData({ ...formData, pause_end: e.target.value })}
                    step="300"
                  />
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                Leave empty if no break needed. No slots will be created during this time.
              </p>
            </div>

            {/* Preview */}
            <div style={{
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: '8px',
              marginTop: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Preview:</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Creating {formData.dates.filter(d => d).length} date(s) from {formData.start_time} to {formData.end_time} with {formData.duration_minutes} minute slots
              </p>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#e5e5e5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="cta-button"
                disabled={loading}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {loading ? 'Creating...' : 'Create Availability'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Multi Date Picker Modal */}
      {showDatePicker && (
        <MultiDatePicker
          selectedDates={formData.dates}
          onDatesChange={(dates) => setFormData({ ...formData, dates })}
          onClose={() => setShowDatePicker(false)}
        />
      )}
    </div>
  );
};

export default AvailabilityCreatePage;
